import React, { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import flashMessage from '../../components/HelperFunctions';
import { Alert, Button, EmptyState, Skeleton, StatusBadge } from '../ui/Primitives';
import {
  createOrganizationInvitation,
  deleteOrganizationMember,
  getOrganization,
  getOrganizationInvitations,
  getOrganizationMembers,
  resendOrganizationInvitation,
  revokeOrganizationInvitation,
  updateOrganizationMember,
} from '../../services/organizationApi';
import { formatAmericanPhoneNumber } from '../../utils/phoneNumbers';
import { SUPPORTED_TIMEZONES } from '../../utils/timezones';
import './ManageOrgMembersSection.css';


const NOTIFICATION_LABELS = {
  never: 'Never',
  'once-end-of-day': 'Daily digest',
  'as-completed': 'After each call',
};

const NOTIFICATION_TABLE_LABELS = {
  never: 'Never',
  'once-end-of-day': 'Daily digest',
  'as-completed': 'Each call',
};

const DAILY_REPORT_TIMES = Array.from({ length: 48 }, (_unused, index) => {
  const hour = Math.floor(index / 2);
  const minute = index % 2 ? 30 : 0;
  const value = `${String(hour).padStart(2, '0')}:${String(minute).padStart(2, '0')}`;
  return {
    value,
    label: `${hour % 12 || 12}:${String(minute).padStart(2, '0')} ${hour < 12 ? 'AM' : 'PM'}`,
  };
});


export function formatPhoneInput(value) {
  const original = String(value || '').trim();
  const enteredDigits = original.replace(/\D/g, '');
  if (original.startsWith('+') && enteredDigits && !enteredDigits.startsWith('1')) return original;
  const digits = (
    enteredDigits.length === 11 && enteredDigits.startsWith('1')
      ? enteredDigits.slice(1)
      : enteredDigits
  ).slice(0, 10);
  if (digits.length <= 3) return digits;
  if (digits.length <= 6) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
  return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
}


export function memberRoutingStatus(member) {
  if (!member.can_receive_transfers) {
    return { label: 'Off', status: 'neutral' };
  }
  if (!String(member.phone || '').trim()) {
    return { label: 'Needs phone', status: 'warning' };
  }
  return { label: 'Ready', status: 'success' };
}


export function memberMessageEligible(member) {
  return Boolean(member.id);
}


function accessLabel(member) {
  const access = member.platform_access || (
    member.is_org_admin || member.is_admin
      ? 'administrator'
      : member.is_active ? 'member' : 'directory_only'
  );
  if (access === 'administrator') return 'Administrator';
  if (access === 'member') return 'Member';
  return 'Directory only';
}


function sortableValue(member, column) {
  if (column === 'name') return `${member.last_name || ''}, ${member.first_name || ''}`;
  if (column === 'routing') return memberRoutingStatus(member).label;
  if (column === 'phone') return String(member.phone || '');
  if (column === 'access') return accessLabel(member);
  if (column === 'notifications') return NOTIFICATION_LABELS[member.notifications] || 'Never';
  return String(member[column] || '');
}


function SortHeader({ column, label, sorting, onSort, className = '' }) {
  const active = sorting.id === column;
  return (
    <th className={className} aria-sort={active ? (sorting.desc ? 'descending' : 'ascending') : 'none'}>
      <button type="button" className="team-sort-button" onClick={() => onSort(column)}>
        {label}
        <span aria-hidden="true">{active ? (sorting.desc ? '↓' : '↑') : '↕'}</span>
      </button>
    </th>
  );
}


function EditIcon() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path d="M12 20h9" />
      <path d="M16.5 3.5a2.1 2.1 0 0 1 3 3L8 18l-4 1 1-4Z" />
    </svg>
  );
}


function RemoveIcon() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path d="M3 6h18" />
      <path d="M8 6V4h8v2" />
      <path d="M19 6l-1 14H6L5 6" />
      <path d="M10 11v5M14 11v5" />
    </svg>
  );
}


function InviteIcon() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path d="M4 5h16v14H4Z" />
      <path d="m4 7 8 6 8-6" />
      <path d="M18 3v4M16 5h4" />
    </svg>
  );
}


function apiErrorMessage(error, fallback) {
  const data = error?.response?.data;
  if (typeof data?.error?.message === 'string') return data.error.message;
  if (typeof data?.message === 'string') return data.message;
  if (error?.response?.status === 403) return 'You do not have permission to manage this team.';
  if (error?.response?.status === 409) return 'That change conflicts with an existing team member.';
  return fallback;
}


function invitationDate(value) {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  return new Intl.DateTimeFormat(undefined, { dateStyle: 'medium' }).format(date);
}


function memberFormValues(member = null) {
  return {
    first_name: member?.first_name || '',
    last_name: member?.last_name || '',
    role_department: member?.role_department || '',
    email: member?.email || '',
    phone: formatPhoneInput(member?.phone || ''),
    ext: member?.ext || '',
    can_receive_transfers: Boolean(member?.can_receive_transfers),
    notifications: member?.notifications || 'never',
    use_custom_daily_report_schedule: Boolean(member?.daily_report_time && member?.daily_report_timezone),
    daily_report_time: member?.daily_report_time || '',
    daily_report_timezone: member?.daily_report_timezone || '',
    access_level: member?.is_org_admin || member?.is_admin ? 'administrator' : 'member',
  };
}


function MemberModal({ member = null, invitationSeed = null, organization = null, onClose, onSubmit }) {
  const [values, setValues] = useState(() => memberFormValues(member || invitationSeed));
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState('');
  const headingId = member ? 'edit-team-member-heading' : 'invite-team-member-heading';

  const update = (key, value) => setValues(current => ({ ...current, [key]: value }));

  const handleSubmit = async event => {
    event.preventDefault();
    if (values.can_receive_transfers && !values.phone.trim()) {
      setError('Add a phone number before enabling call transfers.');
      return;
    }
    setIsSaving(true);
    setError('');
    try {
      const { use_custom_daily_report_schedule, ...submittedValues } = values;
      if (member) {
        submittedValues.daily_report_time = use_custom_daily_report_schedule ? values.daily_report_time : null;
        submittedValues.daily_report_timezone = use_custom_daily_report_schedule ? values.daily_report_timezone : null;
      } else {
        delete submittedValues.daily_report_time;
        delete submittedValues.daily_report_timezone;
      }
      await onSubmit(submittedValues);
      onClose();
    } catch (submitError) {
      setError(apiErrorMessage(submitError, 'The team member could not be saved.'));
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="modal-overlay" role="presentation">
      <div className="modal-content team-member-modal" role="dialog" aria-modal="true" aria-labelledby={headingId}>
        <div className="team-member-modal-heading">
          <div>
            <span className="say-eyebrow">Team &amp; call routing</span>
            <h2 id={headingId}>{member ? 'Edit team member' : 'Invite team member'}</h2>
          </div>
          <button className="team-modal-close" type="button" aria-label="Close" onClick={onClose} disabled={isSaving}>×</button>
        </div>

        <form onSubmit={handleSubmit}>
          {error ? <Alert tone="danger">{error}</Alert> : null}

          <div className="team-form-grid">
            <label htmlFor="team-first-name">First name
              <input id="team-first-name" className="modal-input" value={values.first_name} onChange={event => update('first_name', event.target.value)} autoComplete="given-name" required />
            </label>
            <label htmlFor="team-last-name">Last name
              <input id="team-last-name" className="modal-input" value={values.last_name} onChange={event => update('last_name', event.target.value)} autoComplete="family-name" required />
            </label>
            <label className="team-form-span" htmlFor="team-role">Role or department
              <input id="team-role" className="modal-input" value={values.role_department} onChange={event => update('role_department', event.target.value)} placeholder="Sales, Support, Office manager…" />
              <span className="team-field-help">Helps the receptionist route requests such as “Connect me to Support.”</span>
            </label>
            <label className="team-form-span" htmlFor="team-email">Email
              <input id="team-email" className="modal-input" type="email" value={values.email} onChange={event => update('email', event.target.value)} autoComplete="email" required />
            </label>
            <label htmlFor="team-phone">Transfer phone
              <input id="team-phone" className="modal-input" type="tel" inputMode="tel" autoComplete="tel" placeholder="(555) 123-4567" value={values.phone} onChange={event => update('phone', formatPhoneInput(event.target.value))} required={values.can_receive_transfers} />
            </label>
            <label htmlFor="team-extension">Extension
              <input id="team-extension" className="modal-input" inputMode="numeric" value={values.ext} onChange={event => update('ext', event.target.value.replace(/\D/g, '').slice(0, 10))} />
            </label>
          </div>

          <label className="team-toggle-row" htmlFor="team-routing-enabled">
            <span>
              <strong>Available for call transfers</strong>
              <small>Include this person in the receptionist’s live call-routing directory.</small>
            </span>
            <input id="team-routing-enabled" type="checkbox" checked={values.can_receive_transfers} onChange={event => update('can_receive_transfers', event.target.checked)} />
          </label>

          <label className="team-summary-field" htmlFor="team-notifications">Call summary emails
            <select id="team-notifications" className="say-select" value={values.notifications} onChange={event => update('notifications', event.target.value)}>
              <option value="never">Never</option>
              <option value="once-end-of-day">Daily digest</option>
              <option value="as-completed">After each call</option>
            </select>
          </label>

          {values.notifications === 'once-end-of-day' && member ? (
            <div className="team-daily-report-schedule">
              <label className="team-toggle-row" htmlFor="team-custom-daily-schedule">
                <span>
                  <strong>Use a personal daily schedule</strong>
                  <small>Otherwise this member follows the organization default.</small>
                </span>
                <input
                  id="team-custom-daily-schedule"
                  type="checkbox"
                  checked={values.use_custom_daily_report_schedule}
                  onChange={event => {
                    const enabled = event.target.checked;
                    setValues(current => ({
                      ...current,
                      use_custom_daily_report_schedule: enabled,
                      daily_report_time: enabled
                        ? current.daily_report_time || organization?.daily_report_time || '17:00'
                        : current.daily_report_time,
                      daily_report_timezone: enabled
                        ? current.daily_report_timezone || organization?.daily_report_timezone || organization?.timezone || 'America/Los_Angeles'
                        : current.daily_report_timezone,
                    }));
                  }}
                />
              </label>
              {values.use_custom_daily_report_schedule ? (
                <div className="team-form-grid team-daily-report-fields">
                  <label htmlFor="team-daily-report-time">Delivery time
                    <select id="team-daily-report-time" className="say-select" value={values.daily_report_time} onChange={event => update('daily_report_time', event.target.value)} required>
                      {DAILY_REPORT_TIMES.map(option => <option key={option.value} value={option.value}>{option.label}</option>)}
                    </select>
                  </label>
                  <label htmlFor="team-daily-report-timezone">Timezone
                    <select id="team-daily-report-timezone" className="say-select" value={values.daily_report_timezone} onChange={event => update('daily_report_timezone', event.target.value)} required>
                      {(SUPPORTED_TIMEZONES.some(option => option.value === values.daily_report_timezone)
                        ? SUPPORTED_TIMEZONES : [...SUPPORTED_TIMEZONES, { value: values.daily_report_timezone, label: values.daily_report_timezone }]).map(timezone => <option key={timezone.value} value={timezone.value}>{timezone.label}</option>)}
                    </select>
                  </label>
                </div>
              ) : null}
            </div>
          ) : null}

          {values.notifications === 'once-end-of-day' && !member ? (
            <span className="team-field-help team-invitation-schedule-help">This person will start with the organization’s daily digest schedule and can choose a personal schedule after joining.</span>
          ) : null}

          {!member ? (
            <label className="team-summary-field" htmlFor="team-access-level">Sayable access
              <select id="team-access-level" className="say-select" value={values.access_level} onChange={event => update('access_level', event.target.value)}>
                <option value="member">Team member</option>
                <option value="administrator">Organization administrator</option>
              </select>
              <span className="team-field-help">Administrators can manage organization settings, integrations, and other team members.</span>
            </label>
          ) : null}

          <div className="team-access-note">
            <strong>Platform access: {member ? accessLabel(member) : values.access_level === 'administrator' ? 'Administrator' : 'Team member'}</strong>
            {!member ? <span>We’ll email a secure invitation so this person can create their own password.</span> : null}
          </div>

          <div className="say-dialog-actions">
            <Button variant="ghost" type="button" onClick={onClose} disabled={isSaving}>Cancel</Button>
            <Button type="submit" loading={isSaving} loadingLabel={member ? 'Saving…' : 'Sending invitation…'}>{member ? 'Save changes' : 'Send invitation'}</Button>
          </div>
        </form>
      </div>
    </div>
  );
}


function DeleteMemberModal({ member, onClose, onConfirm }) {
  const [isDeleting, setIsDeleting] = useState(false);
  const [error, setError] = useState('');

  const confirm = async () => {
    setIsDeleting(true);
    setError('');
    try {
      await onConfirm(member);
      onClose();
    } catch (deleteError) {
      setError(apiErrorMessage(deleteError, 'The team member could not be removed.'));
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <div className="modal-overlay" role="presentation">
      <div className="modal-content team-delete-modal" role="alertdialog" aria-modal="true" aria-labelledby="remove-team-member-heading">
        <span className="say-eyebrow">Team &amp; call routing</span>
        <h2 id="remove-team-member-heading">Remove {member.first_name || 'team member'}?</h2>
        <p>This removes the person from your organization and live call-routing directory.</p>
        {error ? <Alert tone="danger">{error}</Alert> : null}
        <div className="say-dialog-actions">
          <Button variant="ghost" type="button" onClick={onClose} disabled={isDeleting}>Cancel</Button>
          <Button variant="danger" type="button" loading={isDeleting} loadingLabel="Removing…" onClick={confirm}>Remove member</Button>
        </div>
      </div>
    </div>
  );
}


function ManageOrgMembersSection({ orgMember, initialOrganization = null, messageDelivery = null, refreshVersion = 0, onMemberSaved, onBusyChange }) {
  const [searchParams, setSearchParams] = useSearchParams();
  const [sorting, setSorting] = useState({ id: 'name', desc: false });
  const [expandedMembers, setExpandedMembers] = useState([]);
  const [filtering, setFiltering] = useState('');
  const [pageIndex, setPageIndex] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [members, setMembers] = useState([]);
  const [invitations, setInvitations] = useState([]);
  const [organization, setOrganization] = useState(initialOrganization);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [editingMember, setEditingMember] = useState(null);
  const [invitingMember, setInvitingMember] = useState(null);
  const [deletingMember, setDeletingMember] = useState(null);
  const [isAdding, setIsAdding] = useState(false);
  const [invitationAction, setInvitationAction] = useState('');
  useEffect(() => { onBusyChange?.(Boolean(isLoading || editingMember || invitingMember || deletingMember || isAdding || invitationAction)); }, [isLoading, editingMember, invitingMember, deletingMember, isAdding, invitationAction, onBusyChange]);
  const canManage = Boolean(orgMember?.is_org_admin || orgMember?.is_admin);
  const organizationId = orgMember?.organization_id;
  const currentMemberId = String(orgMember?.id || orgMember?.user_id || '');

  useEffect(() => {
    let active = true;
    async function load() {
      setIsLoading(true);
      setLoadError('');
      try {
        const [nextMembers, nextOrganization, nextInvitations] = await Promise.all([
          getOrganizationMembers(organizationId),
          getOrganization(organizationId),
          canManage ? getOrganizationInvitations(organizationId) : Promise.resolve([]),
        ]);
        if (!active) return;
        setMembers(Array.isArray(nextMembers) ? nextMembers : []);
        setInvitations(Array.isArray(nextInvitations) ? nextInvitations : []);
        setOrganization(nextOrganization);
      } catch (error) {
        if (active) setLoadError(apiErrorMessage(error, 'Your team could not be loaded.'));
      } finally {
        if (active) setIsLoading(false);
      }
    }
    if (organizationId) load();
    return () => { active = false; };
  }, [canManage, organizationId, refreshVersion]);

  const inviteMember = async values => {
    const invitation = await createOrganizationInvitation(organizationId, values);
    setInvitations(current => [invitation, ...current.filter(item => item.id !== invitation.id)]);
    flashMessage(
      invitation.delivery_status === 'sent' ? 'success' : 'error',
      invitation.delivery_status === 'sent'
        ? 'Invitation sent.'
        : 'Invitation created, but the email could not be delivered. Use Resend to try again.',
    );
  };

  const resendInvitation = async invitation => {
    setInvitationAction(`resend:${invitation.id}`);
    try {
      const updated = await resendOrganizationInvitation(organizationId, invitation.id);
      setInvitations(current => current.map(item => item.id === updated.id ? updated : item));
      flashMessage(
        updated.delivery_status === 'sent' ? 'success' : 'error',
        updated.delivery_status === 'sent' ? 'Invitation resent.' : 'The invitation email could not be delivered.',
      );
    } catch (error) {
      flashMessage('error', apiErrorMessage(error, 'The invitation could not be resent.'));
    } finally {
      setInvitationAction('');
    }
  };

  const revokeInvitation = async invitation => {
    setInvitationAction(`revoke:${invitation.id}`);
    try {
      await revokeOrganizationInvitation(organizationId, invitation.id);
      setInvitations(current => current.filter(item => item.id !== invitation.id));
      flashMessage('success', 'Invitation revoked.');
    } catch (error) {
      flashMessage('error', apiErrorMessage(error, 'The invitation could not be revoked.'));
    } finally {
      setInvitationAction('');
    }
  };

  const saveMember = async values => {
    const member = await updateOrganizationMember(organizationId, editingMember.id, values);
    setMembers(current => current.map(item => item.id === member.id ? member : item));
    if (!memberMessageEligible(member) && messageDelivery?.memberIds.includes(String(member.id))) {
      messageDelivery.removeRecipient(member.id);
    }
    flashMessage('success', 'Team member updated.');
    onMemberSaved?.();
  };

  const removeMember = async member => {
    await deleteOrganizationMember(organizationId, member.id);
    setMembers(current => current.filter(item => item.id !== member.id));
    messageDelivery?.removeRecipient(member.id);
    flashMessage('success', 'Team member removed.');
    onMemberSaved?.();
  };

  const filteredMembers = useMemo(() => {
    const query = filtering.trim().toLowerCase();
    const matching = query
      ? members.filter(member => [
        member.first_name,
        member.last_name,
        member.role_department,
        member.email,
        member.phone,
        member.ext,
        accessLabel(member),
        memberRoutingStatus(member).label,
        NOTIFICATION_LABELS[member.notifications],
      ].filter(Boolean).join(' ').toLowerCase().includes(query))
      : members;

    return [...matching].sort((left, right) => {
      const comparison = String(sortableValue(left, sorting.id)).localeCompare(
        String(sortableValue(right, sorting.id)),
        undefined,
        { numeric: true, sensitivity: 'base' },
      );
      return sorting.desc ? -comparison : comparison;
    });
  }, [filtering, members, sorting]);

  const filteredCount = filteredMembers.length;
  const pageCount = Math.max(Math.ceil(filteredCount / pageSize), 1);
  const visibleMembers = filteredMembers.slice(pageIndex * pageSize, (pageIndex + 1) * pageSize);
  const startRecord = filteredCount ? pageIndex * pageSize + 1 : 0;
  const endRecord = Math.min((pageIndex + 1) * pageSize, filteredCount);
  const isBusiness = ['business', 'growth'].includes(organization?.subscription_type);
  const isEnterprise = organization?.subscription_type === 'enterprise';
  const canAddForPlan = isBusiness || isEnterprise;
  const memberEmails = new Set(members.map(member => String(member.email || '').trim().toLowerCase()));
  const pendingInvitations = invitations.filter(invitation => invitation.status === 'pending');
  const pendingSeatCount = pendingInvitations.filter(invitation => (
    !memberEmails.has(String(invitation.email || '').trim().toLowerCase())
  )).length;
  const peopleAndInvitations = members.length + pendingSeatCount;
  const atMemberLimit = isBusiness && peopleAndInvitations >= 50;
  const pendingInvitationEmails = new Set(pendingInvitations.map(invitation => (
    String(invitation.email || '').trim().toLowerCase()
  )));
  const canEditOwnProfile = members.some(member => String(member.id) === currentMemberId);
  const showActions = canManage || canEditOwnProfile;
  const shouldOpenInvitation = searchParams.get('invite') === '1';

  useEffect(() => {
    if (pageIndex >= pageCount) setPageIndex(pageCount - 1);
  }, [pageCount, pageIndex]);

  useEffect(() => {
    if (isLoading || !shouldOpenInvitation) return;
    const nextSearchParams = new URLSearchParams(searchParams);
    nextSearchParams.delete('invite');
    setSearchParams(nextSearchParams, { replace: true });
    if (canManage && canAddForPlan && !atMemberLimit) setIsAdding(true);
  }, [atMemberLimit, canAddForPlan, canManage, isLoading, searchParams, setSearchParams, shouldOpenInvitation]);

  const changeSorting = column => {
    setSorting(current => ({
      id: column,
      desc: current.id === column ? !current.desc : false,
    }));
  };

  if (isLoading) {
    return (
      <section className="organization-members-section team-loading" aria-label="Loading team members">
        <Skeleton width="32%" height={28} />
        <Skeleton height={54} />
        <Skeleton height={54} />
        <Skeleton height={54} />
      </section>
    );
  }

  return (
    <section className="organization-members-section">
      {loadError ? <Alert tone="danger" title="Team unavailable">{loadError}</Alert> : null}
      {!canManage ? <Alert title="Your team profile">You can edit your own directory details. An organization administrator manages invitations and other team members.</Alert> : null}
      {canManage && !canAddForPlan ? <Alert tone="info" title="Add your team on Business">Team call routing is available with Business and Enterprise plans.</Alert> : null}
      {atMemberLimit ? <Alert tone="warning" title="Team member limit reached">Business plans support up to 50 organization members.</Alert> : null}

      <div className="add-new-member-search-container">
        <div className="organization-members-heading">
          <strong>Routing directory</strong>
          <span>{isBusiness ? `${peopleAndInvitations} of 50 seats` : `${members.length} ${members.length === 1 ? 'person' : 'people'}${pendingInvitations.length ? ` · ${pendingInvitations.length} invited` : ''}`}</span>
        </div>
        <label className="team-search-label" htmlFor="team-member-search">
          <span className="sr-only">Search team members</span>
          <input
            id="team-member-search"
            className="org-search-input"
            value={filtering}
            onChange={event => {
              setFiltering(event.target.value);
              setPageIndex(0);
            }}
            placeholder="Search team members"
          />
        </label>
        {canManage && canAddForPlan ? (
          <Button className="team-add-button" onClick={() => setIsAdding(true)} disabled={atMemberLimit}>Invite team member</Button>
        ) : null}
      </div>

      {invitations.length ? (
        <section className="team-invitations" aria-labelledby="pending-invitations-heading">
          <div className="team-invitations-heading"><div><strong id="pending-invitations-heading">Team invitations</strong><span>These people can create their own password from the invitation email.</span></div><StatusBadge status="warning">{pendingInvitations.length} pending</StatusBadge></div>
          <div className="team-invitation-list">
            {invitations.map(invitation => {
              const name = [invitation.first_name, invitation.last_name].filter(Boolean).join(' ');
              const expired = invitation.status === 'expired';
              const deliveryFailed = invitation.delivery_status === 'failed';
              const resending = invitationAction === `resend:${invitation.id}`;
              const revoking = invitationAction === `revoke:${invitation.id}`;
              return (
                <article className="team-invitation-row" key={invitation.id}>
                  <div className="team-invitation-person"><strong>{name}</strong><span>{invitation.email}</span></div>
                  <div className="team-invitation-role"><span>{invitation.role_department || 'Team member'}</span><small>{invitation.access_level === 'administrator' ? 'Administrator access' : 'Team member access'}</small></div>
                  <div className="team-invitation-state"><StatusBadge status={deliveryFailed || expired ? 'warning' : 'success'}>{deliveryFailed ? 'Email failed' : expired ? 'Expired' : 'Sent'}</StatusBadge><small>{expired ? `Expired ${invitationDate(invitation.expires_at)}` : `Expires ${invitationDate(invitation.expires_at)}`}</small></div>
                  {canManage ? <div className="team-invitation-actions"><Button variant="secondary" onClick={() => resendInvitation(invitation)} disabled={Boolean(invitationAction)} loading={resending} loadingLabel="Resending…">Resend</Button><Button variant="ghost" onClick={() => revokeInvitation(invitation)} disabled={Boolean(invitationAction)} loading={revoking} loadingLabel="Revoking…">Revoke</Button></div> : null}
                </article>
              );
            })}
          </div>
        </section>
      ) : null}

      {!loadError && members.length === 0 ? (
        <EmptyState
          icon="↗"
          title="Build your call-routing directory"
          description="Invite the people and departments callers may ask to reach."
          action={canManage && canAddForPlan ? <Button onClick={() => setIsAdding(true)}>Invite team member</Button> : null}
        />
      ) : members.length > 0 ? (
        <>
          <label className="team-mobile-sort" htmlFor="team-mobile-sort">
            Sort by
            <select id="team-mobile-sort" className="say-select" value={`${sorting.id}:${sorting.desc ? 'desc' : 'asc'}`} onChange={event => {
              const [id, direction] = event.target.value.split(':');
              setSorting({ id, desc: direction === 'desc' });
              setPageIndex(0);
            }}>
              {Object.entries({ name: 'Name', role_department: 'Role / department', routing: 'Routing', phone: 'Phone', access: 'Access', notifications: 'Summary emails' }).flatMap(([id, label]) => [
                <option key={`${id}:asc`} value={`${id}:asc`}>{label}: ascending</option>,
                <option key={`${id}:desc`} value={`${id}:desc`}>{label}: descending</option>,
              ])}
            </select>
          </label>
          <div className="organization-table-scroll">
            <table className={`team-members-table${showActions ? ' is-manageable' : ''}${messageDelivery ? ' has-message-delivery' : ''}`}>
              <colgroup>
                <col className="team-col-name" />
                <col className="team-col-role" />
                <col className="team-col-routing" />
                <col className="team-col-phone" />
                <col className="team-col-access" />
                <col className="team-col-notifications" />
                {messageDelivery ? <col className="team-col-messages" /> : null}
                {showActions ? <col className="team-col-actions" /> : null}
              </colgroup>
              <thead>
                <tr>
                  <SortHeader className="team-name-heading" column="name" label="Name" sorting={sorting} onSort={changeSorting} />
                  <SortHeader column="role_department" label="Role / department" sorting={sorting} onSort={changeSorting} />
                  <SortHeader column="routing" label="Routing" sorting={sorting} onSort={changeSorting} />
                  <SortHeader column="phone" label="Phone" sorting={sorting} onSort={changeSorting} />
                  <SortHeader column="access" label="Access" sorting={sorting} onSort={changeSorting} />
                  <SortHeader column="notifications" label="Summary emails" sorting={sorting} onSort={changeSorting} />
                  {messageDelivery ? <th className="team-messages-heading">General messages</th> : null}
                  {showActions ? <th className="team-actions-heading">Actions</th> : null}
                </tr>
              </thead>
              <tbody>
                {visibleMembers.map(member => {
                  const routing = memberRoutingStatus(member);
                  const phone = formatAmericanPhoneNumber(member.phone) || '—';
                  const isAdministrator = member.is_org_admin || member.is_admin;
                  const memberName = [member.first_name, member.last_name].filter(Boolean).join(' ');
                  const messageEligible = memberMessageEligible(member);
                  const hasPendingInvitation = pendingInvitationEmails.has(String(member.email || '').trim().toLowerCase());
                  const isCurrentMember = String(member.id) === currentMemberId;
                  const receivesGeneralMessages = messageEligible && (
                    messageDelivery?.strategy === 'all_active'
                    || messageDelivery?.memberIds.includes(String(member.id))
                  );
                  return (
                  <tr key={member.id} className={expandedMembers.includes(member.id) ? 'team-member-expanded' : ''}>
                    <td data-label="Name" className="team-name-cell" title={memberName}>
                      <div className="team-member-heading">
                        <div><strong className="team-member-name">{memberName}</strong><span className="team-mobile-role">{member.role_department || 'Team member'} · Routing {routing.label.toLowerCase()}</span></div>
                        <button type="button" className="team-mobile-expand" aria-expanded={expandedMembers.includes(member.id)} aria-label={`${expandedMembers.includes(member.id) ? 'Hide' : 'Show'} details for ${memberName}`} onClick={() => setExpandedMembers(current => current.includes(member.id) ? current.filter(id => id !== member.id) : [...current, member.id])}>
                          <span aria-hidden="true">{expandedMembers.includes(member.id) ? '−' : '+'}</span>
                        </button>
                      </div>
                    </td>
                    <td data-label="Role / department" className="team-role-cell" title={member.role_department || 'Not specified'}>{member.role_department || 'Not specified'}</td>
                    <td data-label="Routing" className="team-routing-cell"><StatusBadge status={routing.status}>{routing.label}</StatusBadge></td>
                    <td data-label="Phone" className="team-phone-cell">{phone}{member.ext ? ` ext. ${member.ext}` : ''}</td>
                    <td data-label="Access">{accessLabel(member)}</td>
                    <td data-label="Summary emails">{NOTIFICATION_TABLE_LABELS[member.notifications] || 'Never'}</td>
                    {messageDelivery ? <td data-label="General messages" className="team-messages-cell">
                      {!messageEligible ? (
                        <StatusBadge status="neutral">Unavailable</StatusBadge>
                      ) : messageDelivery.strategy === 'all_active' || !messageDelivery.canManage ? (
                        <StatusBadge status={receivesGeneralMessages ? 'success' : 'neutral'}>
                          {receivesGeneralMessages ? 'Included' : 'Not included'}
                        </StatusBadge>
                      ) : (
                        <label className="team-message-switch">
                          <input
                            type="checkbox"
                            checked={receivesGeneralMessages}
                            disabled={messageDelivery.isSaving}
                            onChange={() => messageDelivery.toggleRecipient(member.id)}
                            aria-label={`Allow general message access for ${memberName}`}
                          />
                          <span aria-hidden="true" />
                          <small>{receivesGeneralMessages ? 'On' : 'Off'}</small>
                        </label>
                      )}
                    </td> : null}
                    {showActions ? (
                      <td data-label="Actions">
                        <div className="team-row-actions">
                          {canManage && !member.is_active && !hasPendingInvitation ? (
                            <button type="button" onClick={() => setInvitingMember(member)} aria-label={`Invite ${memberName} to Sayable`} title={`Invite ${memberName} to Sayable`}>
                              <InviteIcon />
                            </button>
                          ) : null}
                          {canManage || isCurrentMember ? (
                            <button type="button" onClick={() => setEditingMember(member)} aria-label={`Edit ${isCurrentMember ? 'your profile' : memberName}`} title={`Edit ${isCurrentMember ? 'your profile' : memberName}`}>
                              <EditIcon />
                            </button>
                          ) : null}
                          {canManage && !isAdministrator ? (
                            <button type="button" className="team-remove-button" onClick={() => setDeletingMember(member)} aria-label={`Remove ${memberName}`} title={`Remove ${memberName}`}>
                              <RemoveIcon />
                            </button>
                          ) : null}
                        </div>
                      </td>
                    ) : null}
                  </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          <div className="organization-table-footer">
            <div className="say-pagination-controls" role="group" aria-label="Team member pages">
              <button className="say-pagination-button" disabled={pageIndex === 0} onClick={() => setPageIndex(current => Math.max(current - 1, 0))}>Previous</button>
              <button className="say-pagination-button" disabled={pageIndex >= pageCount - 1} onClick={() => setPageIndex(current => Math.min(current + 1, pageCount - 1))}>Next</button>
            </div>
            <div className="say-pagination-summary" aria-live="polite">
              {filteredCount ? `Showing ${startRecord}–${endRecord} of ${filteredCount}` : 'No matching team members'}
            </div>
            <div className="say-page-size-control">
              <label htmlFor="member-page-size">Rows per page</label>
              <select className="say-select" id="member-page-size" value={pageSize} onChange={event => {
                setPageSize(Number(event.target.value));
                setPageIndex(0);
              }}>
                <option value="10">10</option>
                <option value="25">25</option>
                <option value="50">50</option>
              </select>
            </div>
          </div>
        </>
      ) : null}

      {editingMember ? <MemberModal member={editingMember} organization={organization} onClose={() => setEditingMember(null)} onSubmit={saveMember} /> : null}
      {invitingMember ? <MemberModal invitationSeed={invitingMember} organization={organization} onClose={() => setInvitingMember(null)} onSubmit={inviteMember} /> : null}
      {deletingMember ? <DeleteMemberModal member={deletingMember} onClose={() => setDeletingMember(null)} onConfirm={removeMember} /> : null}
      {isAdding ? <MemberModal organization={organization} onClose={() => setIsAdding(false)} onSubmit={inviteMember} /> : null}
    </section>
  );
}


export default ManageOrgMembersSection;
